// React
import { FunctionComponent, useCallback, useEffect, useMemo, useRef, useState } from "react";
// Components
import CodeEditor from "../../components/CodeEditor/CodeEditor";
import PanelCard from "../../components/CodeEditor/PanelCard";
import LoadingButton from "../../components/LoadingButton/LoadingButton";
import { ToastQueueProvider } from "../../components/ToastQueueProvider/ToastQueueProvider";
import ResizableContainer from "../../components/ResizableSection/ResizableContainer";
import JanusPage from "../../components/JanusPage/JanusPage";
import ConfigModal from "../../components/ConfigModal/ConfigModal";
import StructureMapVisualEditor, {
  getStructureMapValidationErrors,
} from "../../components/StructureMapVisualEditor/StructureMapVisualEditor";
// Fhir Front Library
import { FhirStatus, StatusTag, Title } from "@fyrstain/hl7-front-library";
import { TestableWidget } from "@fyrstain/fhir-front-library";
// FHIR
import Client from "fhir-kit-client";
// Resource StructureMap
import { StructureMap, Parameters } from "fhir/r5";
// Translation
import i18n from "i18next";
// React Bootstrap
import { Card, Form, Button } from "react-bootstrap";
// Navigation
import { useNavigate, useParams } from "react-router-dom";
// Styles
import styles from "./StructureMapDetails.module.css";
// Buffer
import { Buffer } from "buffer";
// FontAwesome
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSave,
  faGear,
  faPlay,
  faArrowsRotate,
  faEye,
  faEyeSlash,
  faTriangleExclamation,
  faRotateRight,
} from "@fortawesome/free-solid-svg-icons";

////////////////////////////////
//         Interfaces         //
////////////////////////////////

interface inputs {
  validationReportID: any;
  validationReportStatus: string;
  name: string;
  type: string;
  mode: string;
  content?: string;
  profile?: string;
}

interface structure {
  alias: string;
  mode: string;
  url: string;
}

function getMainGroup(structureMap: StructureMap): any {
  if (!structureMap.group || structureMap.group.length === 0) {
    return undefined;
  }

  return structureMap.group.find((group) => group.name === "main") ?? structureMap.group[0];
}

function getStructures(structureMap: StructureMap): Array<structure> {
  return structureMap.structure as Array<structure>;
}

function getInputs(mainGroup: any, mode: string): Array<inputs> {
  return mainGroup.input.filter((entry: inputs) => entry.mode === mode);
}

function processInputs(mainGroup: any, mode: string, contents: Array<inputs>, structures: Array<structure>) {
  if (!mainGroup?.input) {
    return [];
  }

  return getInputs(mainGroup, mode).map((input) => {
    const newInput = { ...input };
    const currentContent = contents.find((content) => content.name === input.name);
    newInput.content = currentContent?.content || undefined;
    const profile = structures.find((structure) => structure.alias === input.name && structure.mode === input.mode);
    newInput.profile = profile?.url || undefined;
    return newInput;
  });
}

const StructureMapDetail: FunctionComponent = () => {
  /////////////////////////////////////
  //      Constants / ValueSet       //
  /////////////////////////////////////

  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  // StructureMap informations
  const { structureMapId } = useParams();
  const [name, setName] = useState("");
  const [status, setStatus] = useState("");
  const [description, setDescription] = useState("");

  // Contents &  Resources
  const [structureMapContent, setStructureMapContent] = useState("");
  const [structuremapResource, setStructuremapResource] = useState({} as StructureMap);
  const [initialStructureMapContent, setInitialStructureMapContent] = useState("");
  const [sourceContent, setSourceContent] = useState(new Array<inputs>());
  const [targetContent, setTargetContent] = useState(new Array<inputs>());
  const [outputContent, setOutputContent] = useState(new Array<inputs>());
  const [initialOutputContent, setInitialOutputContent] = useState(new Array<inputs>());

  // To enable or disabled the "play" button when the user has modified the Structure Map content
  const [isModified, setIsModified] = useState(false);
  const [isVisualEditor, setIsVisualEditor] = useState(false);

  // Show the spinner when it's "true" and hide when "false"
  const [isDataLoading, setIsDataLoading] = useState(false);
  const [isSaveLoading, setIsSaveLoading] = useState(false);
  const [isActualizeLoading, setIsActualizeLoading] = useState(false);
  const [isResetStructureMapLoading, setIsResetStructureMapLoading] = useState(false);
  const [isResetOutputLoading, setIsResetQuestionnaireLoading] = useState(false);

  // To disable the "check" button to the Output field when the user has an error
  const [isFailingOperation, setIsfailingOperation] = useState(false);

  // To show or hide the content/card with the buttonToggle
  const [showSources, setShowSources] = useState(true);
  const [showTargets, setShowTargets] = useState(true);
  const [showOutputs, setShowOutputs] = useState(true);

  // To resize the cards
  const [sourcesFlex, setSourcesFlex] = useState({ flex: 1 });
  const [targetsFlex, setTargetsFlex] = useState({
    flex: 1,
  });
  const [outputsFlex, setOutputsFlex] = useState({ flex: 1 });
  const containerWidthRef = useRef<HTMLDivElement | null>(null);
  const sourcesRef = useRef<HTMLDivElement | null>(null);
  const targetsRef = useRef<HTMLDivElement | null>(null);
  const outputsRef = useRef<HTMLDivElement | null>(null);

  // Creating a Toast using the Component Toast
  const { createToast } = ToastQueueProvider.useToastQueue();

  // StructureMap configurations
  const [config, setConfig] = useState({
    structureMapEndpoint: process.env.REACT_APP_STRUCTUREMAP_URL ?? process.env.REACT_APP_FHIR_URL ?? "",
    terminologyEndpoint: process.env.REACT_APP_TERMINOLOGY_URL ?? process.env.REACT_APP_FHIR_URL ?? "",
  });

  // Show the modal
  const [showModal, setShowModal] = useState(false);

  /////////////////////////////////////
  //             Client              //
  /////////////////////////////////////

  const fhirClient = useMemo(
    () =>
      new Client({
        baseUrl: process.env.REACT_APP_FHIR_URL ?? "fhir",
      }),
    [],
  );

  const fhirOperationClient = new Client({
    baseUrl: process.env.REACT_APP_MAPPING_URL ?? "fhir",
  });

  //////////////////////////////
  //           Error          //
  //////////////////////////////

  const onError = useCallback(
    (error?: unknown) => {
      navigate("/Error", { state: { error } });
    },
    [navigate],
  );

  ////////////////////////////////
  //           Actions          //
  ////////////////////////////////

  /**
   * To check the state of setIsModified (true or false) based on a difference between the initial content and the changes
   */
  useEffect(() => {
    setIsModified(structureMapContent !== initialStructureMapContent);
  }, [structureMapContent, initialStructureMapContent]);

  /**
   * Load StructureMap from the back to populate the fields.
   *
   * @returns the promise of a StructureMap.
   */
  const loadStructureMap = useCallback(async () => {
    try {
      const response = await fhirClient.read({
        resourceType: "StructureMap",
        id: structureMapId ?? "",
      });
      if (response.resourceType !== "StructureMap") {
        throw Error(response.statusText);
      }

      const structureMap: StructureMap = response as StructureMap;
      let stringifiedStructureMap = JSON.stringify(structureMap, null, "\t");

      setName(structureMap.title ? structureMap.title : "N/A");
      setStatus(structureMap.status ? structureMap.status : "N/A");
      setDescription(structureMap.description ? structureMap.description : "N/A");
      setStructureMapContent(stringifiedStructureMap);
      setStructuremapResource(structureMap);
      setInitialStructureMapContent(stringifiedStructureMap);
      setIsModified(false);
      const mainGroup = getMainGroup(structureMap);
      const structures = getStructures(structureMap);
      if (!mainGroup) {
        setSourceContent([]);
        setTargetContent([]);
        setOutputContent([]);
        setInitialOutputContent([]);
        return;
      }

      setSourceContent(processInputs(mainGroup, "source", [], structures));
      setTargetContent(processInputs(mainGroup, "target", [], structures));
      const initialOutput = processInputs(mainGroup, "target", [], structures);
      setOutputContent(initialOutput);
      setInitialOutputContent(initialOutput);
    } catch (error) {
      onError();
    }
  }, [fhirClient, onError, structureMapId]);

  /**
   * To load the StructureMap and switch the state of setLoading
   */
  const load = useCallback(async () => {
    setLoading(true);
    await loadStructureMap();
    setLoading(false);
  }, [loadStructureMap]);

  useEffect(() => {
    void load();
  }, [load]);

  //Functions to show or hide the cards with a ToggleButton
  function toggleSources() {
    setShowSources(!showSources);
  }
  function toggleTargets() {
    setShowTargets(!showTargets);
  }
  function toggleOutputs() {
    setShowOutputs(!showOutputs);
  }

  /**
   * Function to adjust size/flex according to toggle buttons
   */
  useEffect(() => {
    const visibleCards = [showSources, showTargets, showOutputs].filter(Boolean).length;
    // To have the same size for each card when all are visible or adjust the size when one or two are hidden
    const equalFlex = 1 / visibleCards;
    setSourcesFlex({ flex: showSources ? equalFlex : 0 });
    setTargetsFlex({
      flex: showTargets ? equalFlex : 0,
    });
    setOutputsFlex({ flex: showOutputs ? equalFlex : 0 });
  }, [showSources, showTargets, showOutputs]);

  /**
   * Update the source, target, and output contents based on the StructureMap.
   * @param content of the StructureMap to update
   */
  function actualizeWithContent(content: StructureMap) {
    let mainGroup = getMainGroup(content);
    let structures = getStructures(content);

    if (!mainGroup) {
      setSourceContent([]);
      setTargetContent([]);
      setOutputContent([]);
      setInitialOutputContent([]);
      return;
    }

    setSourceContent(processInputs(mainGroup, "source", sourceContent, structures));

    setTargetContent(processInputs(mainGroup, "target", targetContent, structures));

    const newOutputContent = processInputs(mainGroup, "target", outputContent, structures);
    setOutputContent(newOutputContent);
    setInitialOutputContent(newOutputContent);
  }

  /**
   * Function to parse the structureMap content and actualize the current structureMapContent
   */
  function actualize() {
    setIsActualizeLoading(true);
    try {
      const parsedStructureMap = JSON.parse(structureMapContent) as StructureMap;
      const errors = getStructureMapValidationErrors(parsedStructureMap as any);
      if (errors.length) {
        createToast({
          title: "StructureMap invalide",
          icon: faTriangleExclamation,
          body: errors.join("\n"),
        });
        setIsActualizeLoading(false);
        return;
      }
      actualizeWithContent(parsedStructureMap);
      setIsModified(false);
    } catch (error) {
      createToast({
        title: "JSON invalide",
        icon: faTriangleExclamation,
        body: error instanceof Error ? error.message : "Le contenu ne peut pas être lu.",
      });
      setIsActualizeLoading(false);
      return;
    }
    setTimeout(() => {
      setIsActualizeLoading(false);
    }, 200);
  }

  /**
   * Function to decode the resource if it's a type "Binary" and update the content of outputContent
   * by transforming it into a JSON type
   * @param response is an object containing parameters to be parsed.
   */
  function parseResponse(response: Parameters) {
    const newOutputs: any[] = [];

    if (!response.parameter || response.parameter.length === 0) {
      setOutputContent(newOutputs);
      return;
    }

    response.parameter.forEach((param) => {
      const resource = param.resource;

      if (resource?.resourceType === "Binary" && resource.data) {
        const decodedBinary = Buffer.from(resource.data, "base64").toString();

        const isJson =
          resource.contentType?.includes("json") ||
          resource.contentType === "application/json" ||
          resource.contentType === "json";

        const outputName = param.name || "unknown";

        const output = {
          name: outputName,
          content: "",
          validationReportID: "",
          validationReportStatus: "",
          type: "",
          mode: "",
        };

        try {
          output.content = isJson ? JSON.stringify(JSON.parse(decodedBinary), null, "\t") : decodedBinary;
        } catch (err) {
          output.content = decodedBinary;
        }

        newOutputs.push(output);
      }

      // Handle non-Binary parameters (primitive or nested)
      else if (param.valueString || param.valueBoolean || param.valueInteger) {
        const outputName = param.name || "unknown";
        const output = {
          name: outputName,
          content: param.valueString ?? param.valueBoolean?.toString() ?? param.valueInteger?.toString() ?? "",
          validationReportID: "",
          validationReportStatus: "",
          type: "",
          mode: "",
        };
        newOutputs.push(output);
      }
    });
    setOutputContent(newOutputs);
  }

  /**
   * Save the content of the StructureMap
   */
  async function onSave() {
    let parsedStructureMap: StructureMap;
    try {
      parsedStructureMap = JSON.parse(structureMapContent) as StructureMap;
    } catch (error) {
      createToast({
        title: "JSON invalide",
        icon: faTriangleExclamation,
        body: error instanceof Error ? error.message : "Le contenu ne peut pas être lu.",
      });
      return;
    }

    const errors = getStructureMapValidationErrors(parsedStructureMap as any);
    if (errors.length) {
      createToast({
        title: "StructureMap invalide",
        icon: faTriangleExclamation,
        body: errors.join("\n"),
      });
      return;
    }

    setIsSaveLoading(true);
    try {
      await fhirClient.update({
        resourceType: "StructureMap",
        id: structureMapId ?? "",
        body: parsedStructureMap,
      });
      actualizeWithContent(parsedStructureMap);
      setInitialStructureMapContent(JSON.stringify(parsedStructureMap, null, "\t"));
      setIsModified(false);
    } catch (error) {
      onError();
    }
    setIsSaveLoading(false);
  }

  /**
   * Reset the StructureMap content, returning the initial content
   */
  const resetStructureMapContent = () => {
    setIsResetStructureMapLoading(true);
    //setTimeout is used to show the loading spinner
    setTimeout(() => {
      setStructureMapContent(initialStructureMapContent);
      setIsResetStructureMapLoading(false);
    }, 200);
  };

  /**
   * Allows validation even if it's empty or with a space
   * @param value is the content to be checked
   * @returns true if the content is not empty and without spaces
   */
  const isNotBlank = (value: any) => {
    return value.trim().length > 0;
  };

  /**
   * Function using the transform operation to update the state of outputContent
   */
  async function onTransform() {
    setIsDataLoading(true);
    const createEndpointResource = (address: string) => ({
      resourceType: "Endpoint" as const,
      status: "active" as const,
      connectionType: {
        system: "http://terminology.hl7.org/CodeSystem/endpoint-connection-type",
        code: "hl7-fhir-rest",
      },
      payloadType: [
        {
          coding: [
            {
              system: "http://terminology.hl7.org/CodeSystem/endpoint-connection-type",
              code: "hl7-fhir-rest",
              display: "HL7 FHIR",
            },
          ],
        },
      ],
      address,
      header: ["Content-Type: application/json"],
    });

    // Use the parameters structureMap and input
    let parameter: Parameters = {
      resourceType: "Parameters",
      parameter: [
        {
          name: "structureMap",
          resource: JSON.parse(structureMapContent),
        },
        {
          name: "input",
          part: [],
        },
        {
          name: "structureMapEndpoint",
          resource: createEndpointResource(config.structureMapEndpoint),
        },
        {
          name: "terminologyEndpoint",
          resource: createEndpointResource(config.terminologyEndpoint),
        },
      ],
    };
    // Retrieve source content to use it for the operation
    sourceContent.forEach((entry) => {
      if (parameter.parameter && parameter.parameter[1].part && entry.content && isNotBlank(entry.content)) {
        parameter.parameter[1].part.push({
          name: entry.name,
          resource: {
            resourceType: "Binary",
            contentType: getContentType(entry.type),
            data: Buffer.from(entry.content).toString("base64"),
          },
        });
      }
    });
    // Retrieve target content to use it for the operation
    targetContent.forEach((entry) => {
      if (parameter.parameter && parameter.parameter[1].part && entry.content && isNotBlank(entry.content)) {
        parameter.parameter[1].part.push({
          name: entry.name,
          resource: {
            resourceType: "Binary",
            contentType: getContentType(entry.type),
            data: Buffer.from(entry.content).toString("base64"),
          },
        });
      }
    });
    await fhirOperationClient
      .operation({
        name: "transform",
        input: parameter,
      })
      .then((response) => {
        parseResponse(response);
        setIsfailingOperation(false);
      })
      .catch((error) => {
        setIsDataLoading(false);
        setIsfailingOperation(true);
        // See errors in the Output field
        setOutputContent([
          {
            name: "error",
            type: "Error",
            content: error.response?.data ? JSON.stringify(error.response?.data, null, "\t") : error.toString(),
            mode: "",
            validationReportID: "",
            validationReportStatus: "Error",
          },
        ]);
        // A toast when an error occured
        createToast({
          title: i18n.t("text.errorvalidateheader"),
          icon: faTriangleExclamation,
          body: error.response
            ? JSON.stringify(error.response.data.issue[0].diagnostics, null, "\t")
            : i18n.t("text.errorvalidatetext"),
        });
      });
    setIsDataLoading(false);
  }

  /**
   * Returns the contentType depending on the input type.
   *
   * @param type the input type
   * @returns the contentType
   */
  function getContentType(type: string): string {
    switch (type) {
      case "CSV":
        return "text/csv";
      case "XML":
        return "application/xml";
      case "HL7v2":
        return "text/x-hl7-ft";
      default:
        return "application/json";
    }
  }

  // Function to reset the output content, returning the initial content and all the fields
  const resetOutputContent = useCallback(() => {
    setIsResetQuestionnaireLoading(true);
    setTimeout(() => {
      setOutputContent(initialOutputContent);
      setIsResetQuestionnaireLoading(false);
    }, 200);
  }, [initialOutputContent]);

  //////////////////////////////
  //          Content         //
  //////////////////////////////

  return (
    <JanusPage titleKey="structureMap.detail.title" loading={loading} needsLogin={true}>
      <>
        {/* INFORMATION */}
        <div className="testAccordionSection">
          <div className="section">
            <Card className={styles.card}>
              <Card.Header>
                <Title level={2} content={i18n.t("structureMap.detail.information")} />
              </Card.Header>

              <Card.Body className="flexWrap">
                <div className={styles.form}>
                  <div className={styles.formTextLabel}>
                    <Form.Label className={styles.formLabel}>
                      <strong className={styles.label}>ID :</strong>
                    </Form.Label>
                    <Form.Text>{structureMapId}</Form.Text>
                  </div>

                  <div className={styles.formTextLabel}>
                    <Form.Label className={styles.formLabel}>
                      <strong className={styles.label}>{i18n.t("label.name")} :</strong>
                    </Form.Label>
                    <Form.Text>{name}</Form.Text>
                  </div>

                  <div className={[styles.badgeContainer, "flexWrap"].join(" ")}>
                    <Form.Label className={styles.statuslabel}>
                      <strong>{i18n.t("label.status")} :</strong>
                    </Form.Label>
                    <Form.Text className={[styles.formText, styles.tagMargin].join(" ")}>
                      <StatusTag flavor={FhirStatus[status as keyof typeof FhirStatus]} message={status} />
                    </Form.Text>
                  </div>

                  <div className={styles.formTextLabel}>
                    <Form.Label className={styles.formLabel}>
                      <strong className={styles.label}>{i18n.t("label.generaldescription")} :</strong>
                    </Form.Label>
                    <Form.Text>{description}</Form.Text>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </div>

          {/* StructureMap IDE - StructureMap ENGINE */}
          <div className="section">
            <div className="displayFlexCenter">
              {/* INPUT */}
              <Card className={styles.card}>
                <Card.Header className="flexWrapSpaceBetween">
                  <Title level={2} content={i18n.t("structureMap.detail.title")} />
                  <div className="buttonHeaderCard d-flex align-items-center gap-2">
                    <Form.Check
                      type="switch"
                      id="structuremap-editor-mode"
                      label={isVisualEditor ? i18n.t("structureMap.detail.visualEditor") : "JSON"}
                      checked={isVisualEditor}
                      onChange={(event) => setIsVisualEditor(event.target.checked)}
                      aria-label={i18n.t("structureMap.detail.switchEditor")}
                    />
                    <LoadingButton
                      isLoading={isSaveLoading}
                      icon={faSave}
                      onClick={onSave}
                      enabledTooltipText={i18n.t("tooltip.save")}
                    />
                    <LoadingButton
                      isLoading={isActualizeLoading}
                      icon={faArrowsRotate}
                      onClick={actualize}
                      enabledTooltipText={i18n.t("tooltip.actualize")}
                    />
                    <LoadingButton
                      isDisabled={false}
                      enabledTooltipText={i18n.t("tooltip.configuration")}
                      icon={faGear}
                      onClick={() => setShowModal(true)}
                    />
                    <ConfigModal
                      config={config}
                      setConfig={setConfig}
                      show={showModal}
                      onHide={() => setShowModal(false)}
                    />
                    <LoadingButton
                      icon={faRotateRight}
                      isLoading={isResetStructureMapLoading}
                      onClick={resetStructureMapContent}
                      enabledTooltipText={i18n.t("tooltip.reset")}
                    />
                    <LoadingButton
                      icon={faPlay}
                      isLoading={isDataLoading}
                      onClick={onTransform}
                      isDisabled={isModified}
                      disabledTooltipText={i18n.t("tooltip.disabledplaystructuremap")}
                      enabledTooltipText={i18n.t("tooltip.play")}
                      operationName="$transform"
                    />
                  </div>
                </Card.Header>

                <Card.Body>
                  {isVisualEditor ? (
                    <StructureMapVisualEditor content={structureMapContent} onChange={setStructureMapContent} />
                  ) : (
                    <CodeEditor content={structureMapContent} setContent={setStructureMapContent} />
                  )}
                </Card.Body>
              </Card>
            </div>
          </div>

          <div className="section">
            {/* BUTTONS TOGGLE - SHOW OR HIDE CARDS*/}
            <div className="d-flex mb-3 justify-content-center">
              <Button
                variant={showSources ? "primary" : "outline-primary"}
                className={`${"toggleButton"} ${showSources ? styles.noHoverPrimary : styles.noHoverOutlinePrimary}`}
                onClick={toggleSources}
              >
                <FontAwesomeIcon icon={showSources ? faEye : faEyeSlash} className="me-3" />
                {i18n.t("structureMap.flow.sources")}
              </Button>
              <Button
                variant={showTargets ? "primary" : "outline-primary"}
                className={`${"me-3 ms-3 toggleButton"} ${
                  showTargets ? styles.noHoverPrimary : styles.noHoverOutlinePrimary
                }`}
                onClick={toggleTargets}
              >
                <FontAwesomeIcon icon={showTargets ? faEye : faEyeSlash} className="me-3" />
                {i18n.t("structureMap.flow.targets")}
              </Button>
              <Button
                variant={showOutputs ? "primary" : "outline-primary"}
                className={`${"toggleButton"} ${showOutputs ? styles.noHoverPrimary : styles.noHoverOutlinePrimary}`}
                onClick={toggleOutputs}
              >
                <FontAwesomeIcon icon={showOutputs ? faEye : faEyeSlash} className="me-3" />
                {i18n.t("structureMap.detail.outputs")}
              </Button>
            </div>

            <div className="mb-5">
              <ResizableContainer
                containerWidthRef={containerWidthRef}
                cardConfig={[
                  {
                    cardFlex: sourcesFlex,
                    cardRef: sourcesRef,
                    cardTitle: "Sources",
                    showResizerBar: showTargets || showOutputs,
                    resizerBarConfig: {
                      containerWidthRef,
                      leftRef: sourcesRef,
                      rightRef: showTargets ? targetsRef : outputsRef,
                      setLeftFlex: setSourcesFlex,
                      setRightFlex: showTargets ? setTargetsFlex : setOutputsFlex,
                    },
                    cardBodyChildren: (
                      <PanelCard
                        title="Sources"
                        content={sourceContent}
                        setContent={setSourceContent}
                        isFailingOperation={isFailingOperation}
                        contextType="StructureMap"
                        readOnly={false}
                      />
                    ),
                  },
                  {
                    cardFlex: targetsFlex,
                    cardRef: targetsRef,
                    cardTitle: "Targets",
                    showResizerBar: showOutputs,
                    resizerBarConfig: {
                      containerWidthRef,
                      leftRef: targetsRef,
                      rightRef: outputsRef,
                      setLeftFlex: setTargetsFlex,
                      setRightFlex: setOutputsFlex,
                    },
                    cardBodyChildren: (
                      <PanelCard
                        title="Targets"
                        content={targetContent}
                        setContent={setTargetContent}
                        isFailingOperation={isFailingOperation}
                        contextType="StructureMap"
                        readOnly={false}
                      />
                    ),
                  },
                  {
                    cardFlex: outputsFlex,
                    cardRef: outputsRef,
                    cardTitle: "Outputs",
                    loadingButtonConfig: [
                      {
                        isLoading: isResetOutputLoading,
                        icon: faRotateRight,
                        onClick: resetOutputContent,
                        enabledTooltipText: i18n.t("tooltip.reset"),
                      },
                    ],
                    cardBodyChildren: (
                      <PanelCard
                        title="Outputs"
                        content={outputContent}
                        setContent={setOutputContent}
                        isFailingOperation={isFailingOperation}
                        contextType="StructureMap"
                        readOnly={true}
                      />
                    ),
                  },
                ]}
              />
            </div>

            {structuremapResource.url &&
              process.env.REACT_APP_TEST_URL &&
              process.env.REACT_APP_TEST_SERVER_URL &&
              process.env.REACT_APP_R4_SERVER_ID &&
              process.env.REACT_APP_R4_CLIENT_ID && (
                <TestableWidget
                  url={structuremapResource.url}
                  testUrl={process.env.REACT_APP_TEST_URL}
                  testServerUrl={process.env.REACT_APP_TEST_SERVER_URL}
                  serverId={process.env.REACT_APP_R4_SERVER_ID}
                  clientId={process.env.REACT_APP_R4_CLIENT_ID}
                  language={i18n.t}
                  onError={onError}
                />
              )}
          </div>
        </div>
      </>
    </JanusPage>
  );
};

export default StructureMapDetail;
